<template>
    <default-calculator-layout
        :filter-width="filterWidth"
        :output-width="outputWidth"
        :calcFilter="calcFilter"
        :calculator-title="calculatorTitle">
        <div slot="filter">
            <number-input
                v-model="CurrentMortgageBalance"
                :label="labelCurrentMortgageBalance"
                inputmode="decimal"
            />
            <number-input
                v-model="CurrentMonthlyPayment"
                :label="labelCurrentMonthlyPayment"
                inputmode="decimal"
            />
            <number-input
                v-model="NewInterestRate"
                :label="labelNewInterestRate"
                :tooltip-link="InterestRateTooltip"
                symbol="%"
                symbol-aligned="right"
                precision="3"
                maxValue="100.000"
                inputmode="decimal"
            />
            <drop-down
                v-model="LoanTermInYears"
                :label="labelLoanTermInYears"
                :options="LoanTermInYearsList"
                :tooltip-link="LoanTermTooltip"
            />
        </div>
        <div slot="output">
            <div class="loan-comparison">
                <h4><b>Loan comparison:</b></h4>
                <table class="table table-border-none">
                    <tbody>
                        <tr>
                            <td width="70%">Current Monthly Payment:</td>
                            <td width="30%" class="payment text-right">{{ displayCurrentMonthlyPayment }}</td>
                        </tr>
                        <tr>
                            <td>New Monthly Payment:</td>
                            <td class="payment text-right" :class="{ 'red-text': !differenceIncrOrDecr, 'green-text': differenceIncrOrDecr }">
                                {{ displayMonthlyPayment }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="analysis-results">
                <ul>
                    <li v-if="differenceIncrOrDecr !== 0" :class="{ 'green-arrow': differenceIncrOrDecr, 'red-arrow': !differenceIncrOrDecr }">
                        <i class="lt4-ArrowDownCircle"></i>
                        <i class="lt4-ArrowUpCircle"></i>
                        Refinancing <big v-if="!differenceIncrOrDecr">Increases</big><big v-if="differenceIncrOrDecr">Decreases</big> your monthly payment by <span class="result-amount">{{ differenceMonthlyPayment }}</span>
                    </li>
                    <li v-if="differenceIncrOrDecr === 0" class="no-arrow">
                        Refinancing <big>does not change</big> your monthly payment. Try changing your loan term.
                    </li>
                </ul>
            </div>
            <div class="compare-offers">
                <button class="btn btn-blue" type="submit" @click="redirect()">{{ DestinationButtonText }}</button>
            </div>
            <div class="disclosures">
                <advertising-disclosures
                    v-if="DisclosureText"
                    :disclosure-pre-text="disclosurePreText"
                    :disclosure-text="DisclosureText"
                    :disclosure-tokens="DisclosureTokens"
                />
            </div>
            <lt-logo v-if="ltLogo===1">
                <small>NMLS#1136 Terms & Conditions Apply</small>
            </lt-logo>
        </div>
    </default-calculator-layout>
</template>

<script>
    import Accounting from 'accounting';
    import DefaultCalculatorLayout from '~/layout/default-calculator.vue';
    import PaymentHelper from '~/helpers/payment';
    import LoanTermList from '~/constants/loan-term-list';
    import BaseMixin from '~/mixin/base-mixin';
    import DefaultTooltip from '~/constants/tooltip-defaults';
    import LtLogo from '~/components/lt-logo.vue';

    export default {
        props: {
            interestRateToken: {
                default: '15_FIXED_RATE_225K',
            },
            labelCurrentMortgageBalance: {
                default: 'Current Mortgage Balance:',
            },
            labelCurrentMonthlyPayment: {
                default: 'Current Monthly Payment:',
            },
            labelNewInterestRate: {
                default: 'New Interest Rate:',
            },
            labelLoanTermInYears: {
                default: 'New Loan Term:',
            },
            currentMortgageBalance: {
                default: 250000,
            },
            currentMonthlyPayment: {
                default: 1950,
            },
            newInterestRate: {
                default: 2.875,
            },
            loanTermInYears: {
                default: 30,
            },
            disclosureTokens: {
                default: '95',
            },
            disclosureText: {
                default: '',
            },
            disclosurePreText: {
                default: 'Monthly payments are an estimate and are for informational purposes. They do not represent a finance offer. Other tax, license, title, or fees may apply.',
            },
            interestRateTooltip: {
                default: DefaultTooltip.InterestRate,
            },
            loanTermTooltip: {
                default: DefaultTooltip.LoanTerm,
            },
            destinationLinkOrId: {
                default: 'wp-refinance',
            },
            destinationButtonText: {
                default: 'Compare Refinance Offers',
            },
            loanTermInYearsListType: {
                default: 'refinance',
            },
            ltLogo: {
                default: 0,
            },
        },
        data() {
            return {
                InterestRateToken: this.interestRateToken,
                CurrentMortgageBalance: this.currentMortgageBalance,
                CurrentMonthlyPayment: this.currentMonthlyPayment,
                NewInterestRate: this.newInterestRate,
                LoanTermInYears: this.loanTermInYears,
                LoanTermInYearsList: LoanTermList.LoanTermInYearsList[this.loanTermInYearsListType],
                InterestRateTooltip: parseInt(this.hideTooltip, 10) ? '' : this.interestRateTooltip,
                LoanTermTooltip: parseInt(this.hideTooltip, 10) ? '' : this.loanTermTooltip,
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                DestinationLinkOrId: this.destinationLinkOrId,
                DestinationButtonText: this.destinationButtonText,
            };
        },
        computed: {
            displayCurrentMonthlyPayment() {
                return Accounting.formatMoney(this.unformatFigure(this.CurrentMonthlyPayment), { precision: 0 });
            },
            displayMonthlyPayment() {
                return Accounting.formatMoney(this.unformatFigure(this.monthlyPayment), { precision: 0 });
            },
            differenceIncrOrDecr() {
                if (this.unformatFigure(this.CurrentMonthlyPayment) - this.unformatFigure(this.monthlyPayment) === 0) return 0;
                return this.unformatFigure(this.CurrentMonthlyPayment) - this.unformatFigure(this.monthlyPayment) > 0;
            },
            differenceMonthlyPayment() {
                return Accounting.formatMoney(Math.abs(this.unformatFigure(this.CurrentMonthlyPayment) - this.unformatFigure(this.monthlyPayment)), { precision: 0 });
            },
            monthlyPayment() {
                const monthsLeftOnCurrentLoan = PaymentHelper.getMonthsLeft(this.CurrentInterestRate, this.CurrentMortgageBalance, this.CurrentMonthlyPayment);
                return PaymentHelper.monthlyPaymentForRefinance(monthsLeftOnCurrentLoan, this.unformatFigure(this.CurrentMortgageBalance), this.unformatFigure(this.CurrentMonthlyPayment), this.LoanTermInYears, this.unformatFigure(this.NewInterestRate));
            },
        },
        components: {
            LtLogo,
            DefaultCalculatorLayout,
        },
        mixins: [BaseMixin],
    };
</script>
<style src="../../assets/styles/common.scss" lang="scss"></style>
<style src="../../assets/styles/analysis-comparisions-filter.scss" lang="scss" scoped></style>
